import { api } from "@/features/utils/fetch-server";
import type {
  Tool,
  ClientTool,
  ClientToolWithTool,
  AvailableTool,
} from "../types";

export class ToolsAPI {
  /**
   * Get all available tool definitions
   */
  static async list(clientId: string): Promise<{ tools: Tool[] }> {
    return api.get(`/api/configuration/v1/${clientId}/tools`);
  }

  /**
   * Get a specific tool definition
   */
  static async get(clientId: string, id: string): Promise<{ tool: Tool }> {
    return api.get(`/api/configuration/v1/${clientId}/tools/${id}`);
  }
}

export class ClientToolsAPI {
  /**
   * Get all activated tools for the client with their configurations
   */
  static async list(
    clientId: string,
  ): Promise<{ clientTools: ClientToolWithTool[] }> {
    return api.get(`/api/configuration/v1/${clientId}/client-tools`);
  }

  /**
   * Get all available tools (activated and not activated)
   */
  static async listAvailable(
    clientId: string,
  ): Promise<{ available: AvailableTool[] }> {
    return api.get(`/api/configuration/v1/${clientId}/client-tools/available`);
  }

  /**
   * Get a specific client tool configuration
   */
  static async get(
    clientId: string,
    id: string,
  ): Promise<{ clientTool: ClientTool }> {
    return api.get(`/api/configuration/v1/${clientId}/client-tools/${id}`);
  }

  /**
   * Activate a tool for the client
   */
  static async create(
    clientId: string,
    data: Partial<ClientTool>,
  ): Promise<{ clientTool: ClientTool }> {
    return api.post(`/api/configuration/v1/${clientId}/client-tools`, data);
  }

  /**
   * Update a client tool configuration
   */
  static async update(
    clientId: string,
    id: string,
    data: Partial<ClientTool>,
  ): Promise<{ clientTool: ClientTool }> {
    return api.put(
      `/api/configuration/v1/${clientId}/client-tools/${id}`,
      data,
    );
  }

  /**
   * Delete a client tool (deactivate)
   */
  static async delete(clientId: string, id: string): Promise<void> {
    await api.delete(`/api/configuration/v1/${clientId}/client-tools/${id}`);
  }

  /**
   * Toggle a tool enabled/disabled state
   */
  static async toggle(
    clientId: string,
    id: string,
    enabled: boolean,
  ): Promise<{ clientTool: ClientTool }> {
    return api.post(
      `/api/configuration/v1/${clientId}/client-tools/${id}/toggle`,
      { enabled },
    );
  }

  /**
   * Update per-agent configuration
   */
  static async updateAgentConfiguration(
    clientId: string,
    id: string,
    agentId: string,
    agentConfig: {
      enabled?: boolean;
      configuration?: Record<string, unknown>;
    },
  ): Promise<{ clientTool: ClientTool }> {
    return api.put(
      `/api/configuration/v1/${clientId}/client-tools/${id}/agents/${agentId}`,
      agentConfig,
    );
  }

  /**
   * Get per-agent configuration
   */
  static async getAgentConfiguration(
    clientId: string,
    id: string,
    agentId: string,
  ): Promise<{
    agentConfig: {
      enabled?: boolean;
      configuration?: Record<string, unknown>;
    } | null;
  }> {
    return api.get(
      `/api/configuration/v1/${clientId}/client-tools/${id}/agents/${agentId}`,
    );
  }

  /**
   * Delete per-agent configuration
   */
  static async deleteAgentConfiguration(
    clientId: string,
    id: string,
    agentId: string,
  ): Promise<{ clientTool: ClientTool }> {
    return api.delete(
      `/api/configuration/v1/${clientId}/client-tools/${id}/agents/${agentId}`,
    );
  }
}
